import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { DeclarationsAuthService } from './auth/declarations-auth.service';
import { MsalBroadcastService } from '@azure/msal-angular';
import { HealthService } from './core/health.service';
import { Router, RouterOutlet } from '@angular/router';
import { environment } from 'src/environments/environment';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { NavMenuComponent } from './shared/nav-menu.component';
import { NavigationStyle, PREFERRED_NAVIGATION_STYLE } from './shared/constants';
import { FooterComponent } from './core/footer.component';
import { SidebarNavComponent } from './shared/sidebar-nav.component';

@Component({
  selector: 'ad-root',
  imports: [FooterComponent, NavMenuComponent, RouterOutlet, SidebarNavComponent],
  host: {
    '(window:resize)': 'onResize()'
  },
  template: `
    <!-- Toggle between sidebar and top navigation -->
    <!-- On mobile (<992px), always use top navigation regardless of useSidebar setting -->
    @if (useSidebar && !isMobile) {
      <!-- Sidebar Layout -->
      <div class="layout-wrapper">
        <ad-sidebar-nav class="sidebar-fixed" />

        <div class="sidebar-content">
          <div class="main-content-scrollable">
            @if (!isIframe) {
              <router-outlet />
            }
          </div>
          <ad-footer class="footer-fixed" />
        </div>
      </div>
    } @else {
      <!-- Top Navigation Layout (Default) -->
      <div class="layout-wrapper">
        <ad-nav-menu class="navbar-fixed" />

        <div class="main-content-scrollable">
          <!--This is to avoid reload during acquireTokenSilent() because of hidden iframe -->
          @if (!isIframe) {
            <router-outlet />
          }
        </div>

        <ad-footer class="footer-fixed" />
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        height: 100vh;
        overflow: hidden;
      }

      /* Layout wrapper takes full viewport height */
      .layout-wrapper {
        display: flex;
        flex-direction: column;
        height: 100vh;
        overflow: hidden;
      }

      /* Sticky navbar at top */
      .navbar-fixed {
        position: sticky;
        top: 0;
        z-index: 1020;
        flex-shrink: 0;
      }

      /* Sticky footer at bottom */
      .footer-fixed {
        position: sticky;
        bottom: 0;

        flex-shrink: 0;
      }

      /* Main content area - scrollable */
      .main-content-scrollable {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        position: relative;
      }

      /* Sidebar layout adjustments */
      .sidebar-fixed {
        position: fixed;
        top: 0;
        left: 0;
        height: 100vh;
        z-index: 1030;
        flex-shrink: 0;
      }

      .sidebar-content {
        margin-left: 280px;
        display: flex;
        flex-direction: column;
        height: 100vh;
        overflow: hidden;
      }

      /* Scrollable content area in sidebar layout */
      .sidebar-content .main-content-scrollable {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
      }

      @media (max-width: 991.98px) {
        .sidebar-content {
          margin-left: 0;
        }
      }
    `
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroying$ = new Subject<void>();

  protected isIframe: boolean;
  protected isMobile = false;

  // Toggle between sidebar (true) and top navigation (false)
  // Change this to 'true' to use sidebar navigation
  // Note: On mobile (<992px), will automatically use top navigation
  protected useSidebar = PREFERRED_NAVIGATION_STYLE === NavigationStyle.SideBar;

  public userManual = environment.userManual;
  public feedbackForm = environment.feedbackForm;
  public production = environment.production;
  private wasUnavailable = false;

  constructor(
    private logger: NGXLogger,
    private declarationsAuthService: DeclarationsAuthService,
    private msalBroadcastService: MsalBroadcastService,
    private healthService: HealthService,
    private router: Router
  ) {
    //  This is to avoid reload during acquireTokenSilent() because of hidden iframe
    this.isIframe = window !== window.parent && !window.opener;
    this.checkMobileView();
  }

  public login(): void {
    this.declarationsAuthService.login$().subscribe();
  }

  public ngOnInit(): void {
    this.declarationsAuthService.handleRedirectObservable$().subscribe();

    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS))
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.declarationsAuthService.setActiveAccount(payload.account);

        this.logger.log('Logged in');

        this.declarationsAuthService.loggedIn$.next(true);
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroying$)
      )
      .subscribe(() => {
        if (this.declarationsAuthService.checkAndSetActiveAccount()) {
          this.declarationsAuthService.loggedIn$.next(true);
        }
      });

    this.healthService.isBackendAvailableInterval$().subscribe((isAvailable) => {
      if (!isAvailable) {
        this.wasUnavailable = true;
        this.router.navigate(['/unavailable']);
      } else if (this.wasUnavailable) {
        this.wasUnavailable = false;
        this.router.navigate(['/']);
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }

  public onResize(): void {
    this.checkMobileView();
  }

  private checkMobileView(): void {
    this.isMobile = window.innerWidth < 992; // Bootstrap lg breakpoint
  }
}
